// Add / edit a child.
import { useState } from "react";
import { Sparkles, ChevronRight, Lock } from "lucide-react";
import { useLang } from "../../i18n.jsx";
import { api } from "../api.js";
import { AGES, AVATAR_ALL, AVATAR_FAVORITES, ageBucket, exactAgeLabel } from "../constants.js";
import { EmojiAvatar } from "../emoji.jsx";
import { C, FONT } from "../../design-system.js";
import { AdSlot, BazaarSignInCard, SquircleBg } from "../ui.jsx";


// ── Exact-age picker (shared by add + edit) ───────────────────────────────────
// `allowed` (optional) limits which ages can be picked — during an active plan
// only the ages inside that plan's range, since the range is locked until it ends.
function AgePicker({ value, onChange, allowed }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
      {AGES.map(a => {
        const on = value === a;
        const ok = !allowed || allowed.includes(a);
        return (
          <button key={a} onClick={() => ok && onChange(a)} disabled={!ok} className="press"
            style={{ background: on ? `${C.gold}22` : C.bg, border: `1.5px solid ${on ? C.gold : C.border}`, color: on ? C.gold : ok ? C.textMid : C.textDim, borderRadius: 12, padding: "12px 0", fontFamily: FONT, fontSize: 16, cursor: ok ? "pointer" : "not-allowed", opacity: ok ? 1 : 0.4, transition: "background 0.15s, border-color 0.15s, color 0.15s" }}>
            {exactAgeLabel(a)}
          </button>
        );
      })}
    </div>
  );
}

// ── Gender picker (shared by add + edit) — locked like the age range ─────────
const GENDERS = [["girl", "دختر", "👧"], ["boy", "پسر", "👦"]];
function GenderPicker({ value, onChange, locked }) {
  const { t } = useLang();
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
      {GENDERS.map(([key, label, emoji]) => {
        const on = value === key;
        const ok = !locked || on;
        return (
          <button key={key} onClick={() => !locked && onChange(key)} disabled={!ok} className="press"
            style={{ background: on ? `${C.gold}22` : C.bg, border: `1.5px solid ${on ? C.gold : C.border}`, color: on ? C.gold : ok ? C.textMid : C.textDim, borderRadius: 12, padding: "12px 0", fontFamily: FONT, fontSize: 16, cursor: locked ? "default" : "pointer", opacity: ok ? 1 : 0.4, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "background 0.15s, border-color 0.15s, color 0.15s" }}>
            <EmojiAvatar size={22}>{emoji}</EmojiAvatar> {t(label)}
          </button>
        );
      })}
    </div>
  );
}

// ── Add Child Screen (with expandable emoji picker) ───────────────────────────
// `onSignIn` is set only in the Bazaar app before «ورود با بازار»: signing in is
// then the first half of step 1 (product owner, 2026-10-29), and the form below
// waits for it — the child has to belong to someone.
// The avatar grid — ONE component for «افزودن» and «ویرایش». «همه ایموجی‌ها» is a
// tile of the grid itself (product owner, 2026-11-03): right after the emojis, as
// tall as they are, spanning two columns so its label fits — it used to be a small
// outline button up by the title that parents didn't notice.
function AvatarPicker({ avatar, setAvatar, list, showAll, setShowAll }) {
  const { t } = useLang();
  const tile = { position: "relative", minWidth: 44, minHeight: 44, background: "none", border: "none", padding: 0, cursor: "pointer", transition: "all 0.15s", touchAction: "manipulation" };
  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 20, padding: 20, marginBottom: 16 }}>
      <div style={{ color: C.textMid, fontSize: 15, marginBottom: 14 }}>{t("آواتار کودک")}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(52px, 1fr))", gap: 10, maxHeight: showAll ? 300 : "none", overflowY: showAll ? "auto" : "visible", overflow: showAll ? "auto" : "hidden", transition: "all 0.3s" }}>
        {list.map((a, i) => (
          <button key={a} onClick={() => setAvatar(a)} className="press emojiPick"
            style={{ ...tile, aspectRatio: "1", fontSize: 26, animation: showAll ? `popIn 0.25s ease ${i * 0.015}s both` : "none" }}>
            <SquircleBg fill={avatar === a ? `${C.gold}22` : C.bg} stroke={avatar === a ? C.gold : C.border} strokeWidth={2} glow={avatar === a ? `0 0 7px ${C.gold}99` : undefined} />
            <span style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}><EmojiAvatar size={26}>{a}</EmojiAvatar></span>
          </button>
        ))}
        <button onClick={() => setShowAll(!showAll)} className="press"
          style={{ ...tile, gridColumn: "span 2", background: `${C.gold}14`, border: `2px solid ${C.borderGold}`, borderRadius: 16,
            color: showAll ? C.gold : C.goldLight, fontFamily: FONT, fontSize: 13.5, fontWeight: 700 }}>
          {/* a wide tile: the runtime squircle engine shapes it (SquircleBg would stretch) */}
          <span style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", height: "100%", padding: "0 8px", boxSizing: "border-box", whiteSpace: "nowrap" }}>
            {showAll ? t("نمایش کمتر") : <><Sparkles size={15} /> {t("همه ایموجی‌ها")}</>}
          </span>
        </button>
      </div>
    </div>
  );
}

// `ads` = a free family (no active plan) — Adly's card is pinned at the bottom.
export function AddChildScreen({ onBack, onDone, onSignIn, ads = false }) {
  const { t, dir } = useLang();
  const [name, setName] = useState("");
  const [age, setAge] = useState(null);   // the EXACT age (3–10)
  const [gender, setGender] = useState(null);   // "girl" | "boy"
  const [avatar, setAvatar] = useState("🦁");
  const [showAll, setShowAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const list = showAll ? AVATAR_ALL : AVATAR_FAVORITES;

  async function handleDone() {
    if (onSignIn || !name.trim() || !age || !gender || saving) return;
    setSaving(true);
    setError(null);
    try {
      const created = await api("/children", {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), age, gender, avatar }),
      });
      onDone(created);
    } catch (e) {
      setError(e.message || t("خطا در افزودن فرزند"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: FONT }}>
      <div style={{ background: C.bgDeep, padding: "20px 20px 24px", borderBottom: `1px solid ${C.border}` }}>
        <button onClick={onBack} className="press" style={{ background: "none", border: "none", color: C.gold, fontSize: 16, cursor: "pointer", fontFamily: FONT, padding: 0, marginBottom: 16, display: "flex", alignItems: "center", gap: 4 }}>
          <ChevronRight size={18} style={{ transform: dir === "ltr" ? "rotate(180deg)" : "none" }} /> {t("بازگشت")}
        </button>
        <h2 style={{ color: C.cream, fontSize: 22, fontWeight: 800, margin: 0 }}>{t("افزودن فرزند جدید")}</h2>
        <p style={{ color: C.textMid, fontSize: 15, margin: "6px 0 0" }}>{t("اطلاعات کودک شما")}</p>
      </div>

      <div style={{ padding: 20 }}>
        {onSignIn && <BazaarSignInCard onSignIn={onSignIn} title={t("۱. ورود با حساب بازار")} />}
        <div style={onSignIn ? { opacity: 0.4, pointerEvents: "none", filter: "saturate(0.6)" } : undefined} aria-disabled={!!onSignIn}>
        {/* Avatar picker */}
        <AvatarPicker avatar={avatar} setAvatar={setAvatar} list={list} showAll={showAll} setShowAll={setShowAll} />

        {/* Name */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 20, padding: 20, marginBottom: 16 }}>
          <label style={{ color: C.textMid, fontSize: 15, display: "block", marginBottom: 10 }}>{t("نام کودک")}</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder={t("مثلاً آرین، نیلا، رادین...")}
            style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: "13px 14px", color: C.cream, fontFamily: FONT, fontSize: 17, outline: "none", boxSizing: "border-box" }} />
        </div>

        {/* Age */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 20, padding: 20, marginBottom: 16 }}>
          <label style={{ color: C.textMid, fontSize: 15, display: "block", marginBottom: 10 }}>{t("سن کودک")}</label>
          <AgePicker value={age} onChange={setAge} />
          <p style={{ color: C.textDim, fontSize: 12, lineHeight: 1.7, margin: "10px 0 0" }}>{t("قصه‌ها دقیقاً برای همین سن نوشته می‌شوند.")}</p>
        </div>

        {/* Gender */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 20, padding: 20, marginBottom: 28 }}>
          <label style={{ color: C.textMid, fontSize: 15, display: "block", marginBottom: 10 }}>{t("جنسیت کودک")}</label>
          <GenderPicker value={gender} onChange={setGender} />
          <p style={{ color: C.textDim, fontSize: 12, lineHeight: 1.7, margin: "10px 0 0" }}>{t("قهرمانِ قصه‌ها هم‌جنسِ کودک نوشته می‌شود.")}</p>
        </div>

        {error && (
          <div style={{ color: C.red, fontSize: 14, marginBottom: 12, textAlign: "center" }}>{error}</div>
        )}

        <button onClick={handleDone} disabled={saving} className="press celebrate"
          style={{ width: "100%", background: name && age && gender && !saving ? `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` : C.border, color: name && age && gender && !saving ? C.bgDeep : C.textDim, border: "none", borderRadius: 16, padding: "17px 0", fontSize: 18, fontWeight: 800, cursor: name && age && gender && !saving ? "pointer" : "not-allowed", fontFamily: FONT, transition: "all 0.3s" }}>
          {saving ? t("در حال ذخیره...") : name ? t("افزودن {name} ✓", { name }) : t("افزودن فرزند")}
        </button>
        </div>
      </div>
      <AdSlot show={ads} />
    </div>
  );
}

// ── Edit Child Screen (name + emoji editable, age locked while subscribed) ────
export function EditChildScreen({ child, onBack, onSave, ads = false }) {
  const { t, dir } = useLang();
  const [name, setName] = useState(child.name);
  const [avatar, setAvatar] = useState(child.avatar);
  const [showAll, setShowAll] = useState(false);
  // Exact age; a child from before exact ages starts at its range's lower number.
  const origAge = child.age || parseInt(String(child.age_range).split("-")[0], 10) || null;
  const [age, setAge] = useState(origAge);
  const [gender, setGender] = useState(child.gender || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const list = showAll ? AVATAR_ALL : AVATAR_FAVORITES;

  // A BLANK child = the placeholder a plan bought before any child was added sits
  // on (routers/children.placeholder_child). Saving it IS «افزودن فرزند», so name,
  // age and gender are all required, and an age is CHOSEN, not changed — the
  // range lock doesn't apply until there is a range.
  const blank = !child.name;
  // During an active plan the RANGE is locked, so only ages inside it can be picked.
  const ageLocked = !!child.active_subscription && !!child.age;
  const allowedAges = ageLocked ? AGES.filter(a => ageBucket(a) === child.age_range) : null;
  // Gender: fixed during an active plan once chosen; a child from before the field
  // existed (gender null) can always choose it.
  const genderLocked = ageLocked && !!child.gender;
  const changed = blank ? !!(name.trim() && age && gender)
    : name.trim() !== child.name || avatar !== child.avatar || age !== origAge || gender !== (child.gender || null);

  async function handleSave() {
    if (!changed || !name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const body = {};
      if (name.trim() !== child.name) body.name = name.trim();
      if (avatar !== child.avatar) body.avatar = avatar;
      if (age !== origAge) body.age = age;
      if (gender && gender !== child.gender) body.gender = gender;
      await api(`/children/${child.id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      onSave();
    } catch (e) {
      setError(e.message || t("خطا در ذخیره تغییرات"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: FONT }}>
      <div style={{ background: C.bgDeep, padding: "20px 20px 24px", borderBottom: `1px solid ${C.border}` }}>
        <button onClick={onBack} className="press" style={{ background: "none", border: "none", color: C.gold, fontSize: 16, cursor: "pointer", fontFamily: FONT, padding: 0, marginBottom: 16, display: "flex", alignItems: "center", gap: 4 }}>
          <ChevronRight size={18} style={{ transform: dir === "ltr" ? "rotate(180deg)" : "none" }} /> {t("بازگشت")}
        </button>
        <h2 style={{ color: C.cream, fontSize: 22, fontWeight: 800, margin: 0 }}>{blank ? t("افزودن فرزند") : t("ویرایش پروفایل {name}", { name: child.name })}</h2>
        <p style={{ color: C.textMid, fontSize: 15, margin: "6px 0 0" }}>{blank ? t("اشتراک شما آماده است — فقط بگویید برای چه کسی است") : ageLocked ? t("نام و آواتار قابل تغییر است · سن و جنسیت تا پایان اشتراک قفل است") : t("نام، سن، جنسیت و آواتار قابل تغییر است")}</p>
      </div>

      <div style={{ padding: 20 }}>
        {/* Avatar picker */}
        <AvatarPicker avatar={avatar} setAvatar={setAvatar} list={list} showAll={showAll} setShowAll={setShowAll} />

        {/* Name */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 20, padding: 20, marginBottom: 16 }}>
          <label style={{ color: C.textMid, fontSize: 15, display: "block", marginBottom: 10 }}>{t("نام کودک")}</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder={t("نام کودک")}
            style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: "13px 14px", color: C.cream, fontFamily: FONT, fontSize: 17, outline: "none", boxSizing: "border-box" }} />
        </div>

        {/* Age — editable before subscription, locked during */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 20, padding: 20, marginBottom: 16, }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <label style={{ color: C.textMid, fontSize: 15 }}>{t("سن کودک")}</label>
            {ageLocked && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: C.textMid, fontSize: 13 }}>
                <Lock size={13} /> {t("بازهٔ سنی قفل است")}
              </span>
            )}
          </div>
          <AgePicker value={age} onChange={setAge} allowed={allowedAges} />
          <p style={{ color: C.textMid, fontSize: 13, lineHeight: 1.8, margin: "12px 0 0" }}>
            {ageLocked
              ? t("قصه‌ها برای همین سن نوشته می‌شوند. تا پایان اشتراک، سن فقط در همان بازهٔ سنی قابل تغییر است.")
              : t("قصه‌ها دقیقاً برای همین سن نوشته می‌شوند.")}
          </p>
        </div>

        {/* Gender — free before a plan, locked during one (like the age range) */}
        <div style={{ background: C.bgCard, border: `1px solid ${!gender ? C.borderGold : C.border}`, borderRadius: 20, padding: 20, marginBottom: 28 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <label style={{ color: C.textMid, fontSize: 15 }}>{t("جنسیت کودک")}</label>
            {genderLocked && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: C.textMid, fontSize: 13 }}>
                <Lock size={13} /> {t("جنسیت قفل است")}
              </span>
            )}
          </div>
          <GenderPicker value={gender} onChange={setGender} locked={genderLocked} />
          <p style={{ color: !gender ? C.gold : C.textMid, fontSize: 13, lineHeight: 1.8, margin: "12px 0 0" }}>
            {!gender ? t("جنسیت کودک را انتخاب کنید")
              : genderLocked ? t("تا پایان اشتراک، جنسیت قابل تغییر نیست.")
              : t("قهرمانِ قصه‌ها هم‌جنسِ کودک نوشته می‌شود.")}
          </p>
        </div>

        {error && (
          <div style={{ color: C.red, fontSize: 14, marginBottom: 12, textAlign: "center" }}>{error}</div>
        )}

        <button onClick={handleSave} disabled={saving} className="press"
          style={{ width: "100%", background: changed && name.trim() && !saving ? `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` : C.border, color: changed && name.trim() && !saving ? C.bgDeep : C.textDim, border: "none", borderRadius: 16, padding: "17px 0", fontSize: 18, fontWeight: 800, cursor: changed && name.trim() && !saving ? "pointer" : "not-allowed", fontFamily: FONT, transition: "all 0.3s" }}>
          {saving ? t("در حال ذخیره...") : blank ? (name.trim() ? t("افزودن {name} ✓", { name: name.trim() }) : t("افزودن فرزند")) : changed ? t("ذخیره تغییرات ✓") : t("تغییری ایجاد نشده")}
        </button>
      </div>
      <AdSlot show={ads} />
    </div>
  );
}

